<template>
  <div class="keyboard-test">
    <div class="kb-header">
      <h2 class="kb-title"><i class="el-icon-s-operation"></i> 在线键盘测试</h2>
      <div class="kb-header-tip">
        <p>由于浏览器 API 的限制，可能无法测试以下按键和某些快捷键</p>
        <p><span class="log-btn">Menu</span></p>
      </div>
      <p class="kb-desc">在线使用，无需下载，快速检测键盘好坏。按下键盘任意键，下方按键会高亮显示。</p>
    </div>

    <div class="kb-main">
      <div class="keyboard-wrap" :style="{ '--kb-unit': unitPx + 'px' }">
        <div class="kb-keyboard-area">
          <div class="kb-preview" ref="keyboardRef">
          <!-- 主键盘区：按键尽量正方形（高 = 单位宽） -->
          <div class="kb-main-block">
            <div v-for="(row, ri) in keyRows" :key="'row-' + ri" class="kb-row">
              <div
                v-for="(segment, si) in row"
                :key="'seg-' + ri + '-' + si"
                class="kb-segment"
              >
                <div
                  v-for="key in segment"
                  :key="key.code"
                  :class="['key-cap', keyState(key.code)]"
                  :style="{ width: key.w * unitPx + 'px' }"
                >
                  <span class="key-cap-label">{{ key.label }}</span>
                </div>
              </div>
            </div>
          </div>
          <!-- 小键盘区：第一行与大键盘第二行对齐 -->
          <div class="kb-numpad">
            <div
              v-for="key in numpadKeys"
              :key="key.code"
              :class="['key-cap', keyState(key.code)]"
              :style="{
                gridColumn: key.col + ' / span ' + key.w,
                gridRow: key.row + ' / span ' + key.h
              }"
            >
              <span class="key-cap-label">{{ key.label }}</span>
            </div>
          </div>
        </div>
          <div class="kb-right-block">
            <div class="kb-footer">
              <div class="status-color">
              <span class="color active"></span>
              <span>Active</span>
              <span class="color pressed"></span>
              <span>Pressed</span>
              <span class="color never"></span>
              <span>Never</span>
              <button type="button" class="btn-reset" @click="reset">重置</button>
              </div>
              <div class="apm-info">
                <div class="apm-left">
                  <div><span class="apm-label">APM </span><span class="apm-num">{{ apm }}</span></div>
                  <div class="apm-desc">实时每分钟触发次数</div>
                </div>
              </div>
            </div>
            <div class="kb-sound">
              <i class="el-icon-turn-off"></i>
              <span>键盘声音</span>
            </div>
          </div>
        </div>
        <div class="key-log">
          <div class="log-list">
            <div
              v-for="(item, i) in keyLog"
              :key="i"
              class="log-item"
              :class="item.type"
            >
              <span class="log-time">{{ item.time }}</span>
              <span class="log-btn">{{ item.label }}</span>
              <b>{{ item.type === 'keydown' ? 'KeyDown' : 'KeyUp' }}</b>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const U = 1
const U125 = 1.25
const U15 = 1.5
const U175 = 1.75
const U2 = 2
const U225 = 2.25
const U275 = 2.75
const U625 = 6.25

// 主键区按「区块」分：每行可多段，段与段之间留空隙（主键区 | 导航/方向键）
function mainKeyRows() {
  return [
    [ [
      { code: 'Escape', label: 'ESC', w: U },
      { code: 'F1', label: 'F1', w: U }, { code: 'F2', label: 'F2', w: U }, { code: 'F3', label: 'F3', w: U }, { code: 'F4', label: 'F4', w: U },
      { code: 'F5', label: 'F5', w: U }, { code: 'F6', label: 'F6', w: U }, { code: 'F7', label: 'F7', w: U }, { code: 'F8', label: 'F8', w: U },
      { code: 'F9', label: 'F9', w: U }, { code: 'F10', label: 'F10', w: U }, { code: 'F11', label: 'F11', w: U }, { code: 'F12', label: 'F12', w: U },
      { code: 'PrintScreen', label: 'PrtSc', w: U }, { code: 'ScrollLock', label: 'Scroll', w: U }, { code: 'Pause', label: 'Pause', w: U }
    ] ],
    [ [
      { code: 'Backquote', label: '`', w: U }, { code: 'Digit1', label: '1', w: U }, { code: 'Digit2', label: '2', w: U }, { code: 'Digit3', label: '3', w: U },
      { code: 'Digit4', label: '4', w: U }, { code: 'Digit5', label: '5', w: U }, { code: 'Digit6', label: '6', w: U }, { code: 'Digit7', label: '7', w: U },
      { code: 'Digit8', label: '8', w: U }, { code: 'Digit9', label: '9', w: U }, { code: 'Digit0', label: '0', w: U },
      { code: 'Minus', label: '-', w: U }, { code: 'Equal', label: '=', w: U },
      { code: 'Backspace', label: 'Backspace', w: U2 }
    ], [
      { code: 'Insert', label: 'Ins', w: U }, { code: 'Home', label: 'Home', w: U }, { code: 'PageUp', label: 'PgUp', w: U }
    ] ],
    [ [
      { code: 'Tab', label: 'Tab', w: U15 },
      { code: 'KeyQ', label: 'Q', w: U }, { code: 'KeyW', label: 'W', w: U }, { code: 'KeyE', label: 'E', w: U }, { code: 'KeyR', label: 'R', w: U },
      { code: 'KeyT', label: 'T', w: U }, { code: 'KeyY', label: 'Y', w: U }, { code: 'KeyU', label: 'U', w: U }, { code: 'KeyI', label: 'I', w: U },
      { code: 'KeyO', label: 'O', w: U }, { code: 'KeyP', label: 'P', w: U },
      { code: 'BracketLeft', label: '[', w: U }, { code: 'BracketRight', label: ']', w: U }, { code: 'Backslash', label: '\\', w: U15 }
    ], [
      { code: 'Delete', label: 'Del', w: U }, { code: 'End', label: 'End', w: U }, { code: 'PageDown', label: 'PgDn', w: U }
    ] ],
    [ [
      { code: 'CapsLock', label: 'Caps Lock', w: U175 },
      { code: 'KeyA', label: 'A', w: U }, { code: 'KeyS', label: 'S', w: U }, { code: 'KeyD', label: 'D', w: U }, { code: 'KeyF', label: 'F', w: U },
      { code: 'KeyG', label: 'G', w: U }, { code: 'KeyH', label: 'H', w: U }, { code: 'KeyJ', label: 'J', w: U }, { code: 'KeyK', label: 'K', w: U }, { code: 'KeyL', label: 'L', w: U },
      { code: 'Semicolon', label: ';', w: U }, { code: 'Quote', label: "'", w: U },
      { code: 'Enter', label: 'Enter', w: U225 }
    ] ],
    [ [
      { code: 'ShiftLeft', label: 'Shift', w: U225 },
      { code: 'KeyZ', label: 'Z', w: U }, { code: 'KeyX', label: 'X', w: U }, { code: 'KeyC', label: 'C', w: U }, { code: 'KeyV', label: 'V', w: U },
      { code: 'KeyB', label: 'B', w: U }, { code: 'KeyN', label: 'N', w: U }, { code: 'KeyM', label: 'M', w: U },
      { code: 'Comma', label: ',', w: U }, { code: 'Period', label: '.', w: U }, { code: 'Slash', label: '/', w: U },
      { code: 'ShiftRight', label: 'Shift', w: U275 }
    ], [
      { code: 'ArrowUp', label: '↑', w: U }
    ] ],
    [ [
      { code: 'ControlLeft', label: 'Ctrl', w: U125 }, { code: 'MetaLeft', label: 'Win', w: U125 }, { code: 'AltLeft', label: 'Alt', w: U125 },
      { code: 'Space', label: 'Space', w: U625 },
      { code: 'AltRight', label: 'Alt', w: U125 }, { code: 'MetaRight', label: 'Win', w: U125 }, { code: 'ContextMenu', label: 'Menu', w: U125 }, { code: 'ControlRight', label: 'Ctrl', w: U125 }
    ], [
      { code: 'ArrowLeft', label: '←', w: U }, { code: 'ArrowDown', label: '↓', w: U }, { code: 'ArrowRight', label: '→', w: U }
    ] ]
  ]
}

// 小键盘用 Grid 定位，避免 789/456 行间大片空白（+、Enter 跨 2 行）
function numpadKeysGrid() {
  return [
    { code: 'NumLock', label: 'Num', col: 1, row: 1, w: 1, h: 1 },
    { code: 'NumpadDivide', label: '/', col: 2, row: 1, w: 1, h: 1 },
    { code: 'NumpadMultiply', label: '*', col: 3, row: 1, w: 1, h: 1 },
    { code: 'NumpadSubtract', label: '-', col: 4, row: 1, w: 1, h: 1 },
    { code: 'Numpad7', label: '7', col: 1, row: 2, w: 1, h: 1 },
    { code: 'Numpad8', label: '8', col: 2, row: 2, w: 1, h: 1 },
    { code: 'Numpad9', label: '9', col: 3, row: 2, w: 1, h: 1 },
    { code: 'NumpadAdd', label: '+', col: 4, row: 2, w: 1, h: 2 },
    { code: 'Numpad4', label: '4', col: 1, row: 3, w: 1, h: 1 },
    { code: 'Numpad5', label: '5', col: 2, row: 3, w: 1, h: 1 },
    { code: 'Numpad6', label: '6', col: 3, row: 3, w: 1, h: 1 },
    { code: 'Numpad1', label: '1', col: 1, row: 4, w: 1, h: 1 },
    { code: 'Numpad2', label: '2', col: 2, row: 4, w: 1, h: 1 },
    { code: 'Numpad3', label: '3', col: 3, row: 4, w: 1, h: 1 },
    { code: 'NumpadEnter', label: 'Enter', col: 4, row: 4, w: 1, h: 2 },
    { code: 'Numpad0', label: '0', col: 1, row: 5, w: 2, h: 1 },
    { code: 'NumpadDecimal', label: '.', col: 3, row: 5, w: 1, h: 1 }
  ]
}

const KEY_ROWS = mainKeyRows()
const NUMPAD_KEYS = numpadKeysGrid()

// 扁平列表供日志查找 label（主键区已改为按段分行）
function allKeys() {
  const list = []
  KEY_ROWS.forEach(row => {
    row.forEach(segment => segment.forEach(k => list.push(k)))
  })
  NUMPAD_KEYS.forEach(k => list.push(k))
  return list
}
const ALL_KEYS = allKeys()

const MAX_LOG = 8
const APM_WINDOW_MS = 60 * 1000
const UNIT_PX = 34
const MOBILE_UNIT_PX = 17
const TABLET_UNIT_PX = 19

export default {
  name: 'KeyboardTest',
  data() {
    return {
      unitPx: UNIT_PX,
      keyRows: KEY_ROWS,
      numpadKeys: NUMPAD_KEYS,
      allKeysList: ALL_KEYS,
      pressedKeys: new Set(),
      everPressed: new Set(),
      keyLog: [],
      apmTimestamps: []
    }
  },
  computed: {
    apm() {
      const now = Date.now()
      const cutoff = now - APM_WINDOW_MS
      return this.apmTimestamps.filter(t => t > cutoff).length
    }
  },
  mounted() {
    this.syncKeyboardUnit()
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    window.addEventListener('resize', this.syncKeyboardUnit)
    this.$refs.keyboardRef && this.$refs.keyboardRef.focus()
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('resize', this.syncKeyboardUnit)
  },
  methods: {
    syncKeyboardUnit() {
      const width = window.innerWidth || 1024
      if (width <= 480) {
        this.unitPx = MOBILE_UNIT_PX
        return
      }
      if (width <= 768) {
        this.unitPx = TABLET_UNIT_PX
        return
      }
      this.unitPx = UNIT_PX
    },
    keyState(code) {
      if (this.pressedKeys.has(code)) return 'pressed'
      if (this.everPressed.has(code)) return 'active'
      return 'never'
    },
    onKeyDown(e) {
      const code = e.code
      if (!code) return
      if (this.pressedKeys.has(code)) return
      this.pressedKeys.add(code)
      this.everPressed.add(code)
      const now = Date.now()
      this.apmTimestamps.push(now)
      const cutoff = now - APM_WINDOW_MS
      this.apmTimestamps = this.apmTimestamps.filter(t => t > cutoff)
      this.addLog('keydown', code)
    },
    onKeyUp(e) {
      const code = e.code
      if (!code) return
      this.pressedKeys.delete(code)
      this.addLog('keyup', code)
    },
    addLog(type, code) {
      const item = this.allKeysList.find(k => k.code === code)
      const label = item ? item.label : code
      const now = new Date()
      const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}.${String(now.getMilliseconds()).padStart(3, '0')}`
      this.keyLog.unshift({ time, label, type })
      if (this.keyLog.length > MAX_LOG) this.keyLog.pop()
    },
    reset() {
      this.pressedKeys.clear()
      this.everPressed.clear()
      this.keyLog = []
      this.apmTimestamps = []
    }
  }
}
</script>

<style scoped>
/* 明亮模式（默认） */
.keyboard-test {
  padding: 24px 10px;
  max-width: 100%;
  box-sizing: border-box;
  background: #f5f7fa;
  min-height: 100vh;
}
.kb-header {
  margin-bottom: 20px;
  text-align: center;
}
.kb-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
}
.kb-title i { color: #409EFF; }
.kb-desc {
  font-size: 13px;
  color: #606266;
  margin: 0;
  line-height: 1.5;
}
.kb-main {
  display: flex;
  flex-wrap: nowrap;
  gap: 24px;
  justify-content: flex-start;
  align-items: flex-start;
  max-width: 100%;
  width: 100%;
  margin: 0;
}
.keyboard-wrap {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
  padding: 24px 12px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  box-sizing: border-box;
  overflow-x: auto;
}
.kb-keyboard-area {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  flex: 0 0 auto;
  min-width: min-content;
}
.kb-preview {
  display: flex;
  flex-direction: row;
  gap: 12px;
  outline: none;
  margin-bottom: 20px;
}
.kb-right-block {
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-start;
}
.kb-right-block .kb-footer {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}
.kb-right-block .kb-sound {
  margin-top: 0;
}
.kb-main-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.kb-row {
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: stretch;
}
.kb-segment {
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: stretch;
}
.kb-segment .key-cap {
  flex-shrink: 0;
}
/* 主键区按键尽量正方形：高 = 单位宽 */
.kb-main-block .key-cap {
  height: var(--kb-unit, 32px);
  min-height: var(--kb-unit, 32px);
}
/* 小键盘用 Grid，第一行与大键盘第二行对齐（上边距 = 一行键高 + 行间距） */
.kb-numpad {
  display: grid;
  grid-template-columns: repeat(4, var(--kb-unit, 32px));
  grid-template-rows: repeat(5, var(--kb-unit, 32px));
  gap: 4px;
  align-content: start;
  margin-top: calc(var(--kb-unit, 32px) + 6px);
}
.kb-numpad .key-cap {
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}
.key-cap {
  height: var(--kb-unit, 32px);
  min-height: var(--kb-unit, 32px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  border-radius: 4px;
  background: #f0f2f5;
  border: 1px solid #dcdfe6;
  color: #303133;
  transition: background 0.08s, border-color 0.08s, color 0.08s;
  box-sizing: border-box;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 4px;
  text-align: center;
  line-height: 1.2;
}
.key-cap-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;
}
.key-cap.active {
  background: #e4e7ed;
  border-color: #d4d7de;
  color: #303133;
}
.key-cap.pressed {
  background: #409EFF;
  border-color: #409EFF;
  color: #fff;
}
.key-cap.never {
  background: #f0f2f5;
  border-color: #dcdfe6;
  color: #909399;
}
.kb-footer {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e4e7ed;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px 28px;
}
.status-color {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 14px;
  font-size: 13px;
  color: #606266;
}
.status-color .color {
  width: 18px;
  height: 18px;
  border-radius: 4px;
}
.status-color .color.active { background: #409EFF; }
.status-color .color.pressed { background: #c0c4cc; }
.status-color .color.never { background: #e4e7ed; }
.btn-reset {
  margin-left: 4px;
  padding: 8px 18px;
  font-size: 13px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  cursor: pointer;
}
.btn-reset:hover {
  background: #f5f7fa;
  border-color: #c0c4cc;
  color: #303133;
}
.apm-info {
  display: flex;
  align-items: center;
  gap: 16px;
}
.apm-left .apm-label { font-weight: 600; color: #303133; }
.apm-num { font-size: 22px; font-weight: 700; color: #409EFF; }
.apm-desc { font-size: 12px; color: #909399; margin-top: 4px; }
.kb-sound {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #909399;
}
.kb-sound i { font-size: 14px; }
.key-log {
  flex: 0 0 240px;
  width: 240px;
  min-width: 220px;
  max-width: 260px;
  margin-left: auto;
  flex-shrink: 0;
  padding: 14px 16px;
  background: #f5f7fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  box-sizing: border-box;
}
.log-list {
  overflow: visible;
}
.log-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 6px;
  font-size: 11px;
  padding: 6px 0;
  border-bottom: 1px solid #ebeef5;
  color: #606266;
}
.log-item.keydown { color: #303133; }
.log-item.keyup { color: #909399; }
.log-time { color: #909399; flex-shrink: 0; }
.log-btn {
  display: inline-block;
  min-width: 40px;
  padding: 3px 10px;
  text-align: center;
  background: #f0f2f5;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  font-size: 12px;
  color: #303133;
}
.kb-header-tip {
  margin: 10px auto 14px;
  max-width: 640px;
  padding: 0 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
  word-break: break-word;
  text-align: center;
}
.kb-header-tip p { margin: 6px 0; }
.kb-header-tip .log-btn {
  display: inline-block;
  min-width: 40px;
  padding: 2px 10px;
  text-align: center;
  background: #f0f2f5;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  font-size: 12px;
  color: #303133;
}

/* 暗黑模式跟随全局 */
.dark-mode .keyboard-test {
  background: var(--bg-secondary, #1a1a1a);
}
.dark-mode .keyboard-test .kb-title { color: var(--text-primary, #e0e0e0); }
.dark-mode .keyboard-test .kb-desc { color: var(--text-secondary, #909399); }
.dark-mode .keyboard-wrap {
  background: var(--bg-card, #2d2d2d);
  border-color: var(--border-color, #404040);
  box-shadow: none;
}
.dark-mode .key-cap {
  background: #3d3d3d;
  border-color: #4a4a4a;
  color: #fff;
}
.dark-mode .key-cap.active { background: #555; border-color: #666; color: #fff; }
.dark-mode .key-cap.pressed { background: #fff; border-color: #fff; color: #000; }
.dark-mode .key-cap.never { background: #333; border-color: #444; color: #aaa; }
.dark-mode .kb-footer { border-top-color: var(--border-color, #404040); }
.dark-mode .keyboard-test .status-color { color: #b0b0b0; }
.dark-mode .status-color .color.active { background: #fff; }
.dark-mode .status-color .color.pressed { background: #888; }
.dark-mode .status-color .color.never { background: #444; }
.dark-mode .btn-reset {
  background: transparent;
  border-color: #fff;
  color: #fff;
}
.dark-mode .btn-reset:hover { background: #444; color: #fff; }
.dark-mode .apm-left .apm-label { color: #e0e0e0; }
.dark-mode .apm-num { color: #e0e0e0; }
.dark-mode .apm-desc { color: #909399; }
.dark-mode .kb-sound { color: #909399; }
.dark-mode .key-log {
  background: var(--bg-card, #2d2d2d);
  border-color: var(--border-color, #404040);
  box-shadow: none;
}
.dark-mode .log-item { border-bottom-color: var(--border-color, #404040); color: #b0b0b0; }
.dark-mode .log-item.keydown { color: #fff; }
.dark-mode .log-item.keyup { color: #888; }
.dark-mode .log-time { color: #707070; }
.dark-mode .log-btn { background: #404040; border-color: #555; color: #e0e0e0; }
.dark-mode .kb-header-tip { color: var(--text-secondary, #909399); }
.dark-mode .kb-header-tip .log-btn {
  background: var(--bg-secondary, #404040);
  border-color: var(--border-color, #555);
  color: var(--text-primary, #e0e0e0);
}

@media (max-width: 900px) {
  .keyboard-test { padding: 16px 8px 24px; }
  .kb-main { gap: 12px; }
  .keyboard-wrap {
    flex-wrap: wrap;
    padding: 16px 10px;
    gap: 12px;
  }
  .kb-keyboard-area {
    width: 100%;
    min-width: 0;
  }
  .key-log { flex: 1 1 100%; width: 100%; max-width: none; margin-left: 0; }
  .kb-preview {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    width: 100%;
  }
  .kb-right-block {
    width: 100%;
    gap: 12px;
  }
  .kb-row { gap: 12px; }
  .kb-segment { gap: 4px; }
  .kb-numpad {
    margin-top: 0;
  }
  .key-cap {
    font-size: 8px;
    padding: 0 2px;
  }
  .key-cap-label {
    white-space: normal;
    line-height: 1.05;
  }
  .kb-footer {
    gap: 14px;
  }
}

@media (max-width: 480px) {
  .keyboard-wrap {
    padding: 12px 6px;
  }
  .kb-row { gap: 8px; }
  .key-cap {
    font-size: 7px;
    border-radius: 3px;
  }
  .status-color {
    gap: 6px 10px;
  }
  .btn-reset {
    width: 100%;
    margin-left: 0;
  }
}
</style>
